<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { usersStore } from '@/src/store/users';
import useDateFormatter from '@/src/composables/useDateFormatter';
import SeAvatarChatOption from '@/src/ui-kit/avatar-chat-option/AvatarChatOption.vue';
import SeButton from '@/src/ui-kit/button/Button.vue';
import SePopup from '@/src/ui-kit/popup/Popup.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';

const props = withDefaults(
  defineProps<{
    timer: any;
    waitingForChatConfirmation: boolean;
    rejectChatLoading?: boolean;
    acceptChatLoading?: boolean;
  }>(),
  {
    waitingForChatConfirmation: false,
    rejectChatLoading: false,
    acceptChatLoading: false,
  }
);

const emits = defineEmits(['accept', 'reject']);

const userStore = usersStore();
const { currentUser, isAdvisor, isUser } = storeToRefs(userStore);

const visible = computed(() => isAdvisor.value && props.waitingForChatConfirmation);
</script>

<script lang="ts">
export default {
  name: 'SeIncomingCallPopup',
};
</script>

<template>
  <se-popup v-if="visible" :classes="'sm:w-[448px] responsive-stretched'" :show-close="false">
    <div
      class="text-warning-120 bg-warning-10 rounded-12 py-16 px-12 flex flex-col justify-center items-center gap-8 my-24"
    >
      <div class="flex items-center gap-8">
        <se-icon name="clock" />
        <span class="text-h-l font-semibold">{{ useDateFormatter().formatSeconds(props.timer) }}s</span>
      </div>
      <p class="text-h-xs font-bold">Time left to accept request</p>
    </div>
    <div v-if="currentUser">
      <div class="flex flex-column items-center justify-center w-full">
        <se-avatar-chat-option
          :id="currentUser.active_chat.client.id"
          :size="68"
          :name="currentUser.active_chat.client.name"
          :src="currentUser.active_chat.client.avatar"
          :chat-action="currentUser.active_chat.mode"
        />
        <p class="text-h-xl pt-16 text-center font-bold">
          {{ currentUser.active_chat.client.name }} is requesting a
          <span class="capitalize">
            {{ currentUser.active_chat.mode }} {{ currentUser.active_chat.mode === 'text' ? 'chat' : 'call' }}
          </span>
        </p>
      </div>
      <div class="relative bg-neutral-10 rounded-12 flex justify-center mt-24 mb-8 pt-[11px] pb-[15px]">
        <div class="flex flex-col justify-center items-center w-1/2">
          <p class="text-h-xs text-neutral-60 mb-1">Per minute fee</p>
          <p>
            <span
              v-if="
                currentUser.active_chat.actual_fee &&
                currentUser.active_chat.per_minute_fee != currentUser.active_chat.actual_fee
              "
              class="line-through text-neutral-50 text-b-m mr-1"
            >
              ${{ currentUser.active_chat.actual_fee }}/min
            </span>
            <span class="text-h-m font-semibold"> ${{ currentUser.active_chat.per_minute_fee }}/min </span>
          </p>
        </div>
        <div class="divider absolute bg-neutral-30 top-0 w-[0.5px] h-full" />
        <div class="flex flex-col justify-center items-center w-1/2">
          <p class="text-h-xs mb-1 text-neutral-60">Free minutes</p>
          <p class="text-h-m font-bold">
            {{ currentUser.active_chat.free_minutes + currentUser.active_chat.penalty_minutes }} minutes
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <se-button size="large" class="w-full mb-16" :loading="props.acceptChatLoading" @click="emits('accept')">
        Accept
      </se-button>
      <se-button
        size="large"
        class="w-full"
        type="tertiary"
        :loading="props.rejectChatLoading"
        @click="emits('reject')"
      >
        Reject
      </se-button>
    </template>
  </se-popup>
</template>

<style lang="scss"></style>
